import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  makeStyles,
  IconButton,
  Collapse,
  Box,
  Chip
} from '@material-ui/core';
import {
  Search,
  Clear,
  ExpandMore,
  ExpandLess,
  FilterList
} from '@material-ui/icons';
import { DatePicker } from '@material-ui/pickers';
import axios from 'axios';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%'
  },
  formControl: {
    minWidth: 120,
    width: '100%'
  },
  filterHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2)
  },
  filterChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  }
}));

export default function PlantFilters({ filters, onFilterChange, apiUrl }) {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  const [crews, setCrews] = useState([]);
  const [clients, setClients] = useState([]);
  const [jobSites, setJobSites] = useState([]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      // Fetch all filter options in parallel
      const [crewsRes, clientsRes, jobSitesRes] = await Promise.all([
        axios.get(`${apiUrl}/api/hmi/plant-tracking/filters/crews`),
        axios.get(`${apiUrl}/api/hmi/plant-tracking/filters/clients`),
        axios.get(`${apiUrl}/api/hmi/plant-tracking/filters/job-sites`)
      ]);
      
      // Set crews with 'All' option
      setCrews([
        { _id: 'all', name: 'All Crews' },
        ...(crewsRes.data.crews || [])
      ]);
      
      // Set clients with 'All' option
      setClients([
        { _id: 'all', name: 'All Clients' },
        ...(clientsRes.data.clients || [])
      ]);
      
      // Set job sites with 'All' option
      setJobSites([
        { _id: 'all', name: 'All Job Sites' },
        ...(jobSitesRes.data.jobSites || [])
      ]);
    } catch (error) {
      console.error('Error fetching filter options:', error);
      // Set default options on error
      setCrews([{ _id: 'all', name: 'All Crews' }]);
      setClients([{ _id: 'all', name: 'All Clients' }]);
      setJobSites([{ _id: 'all', name: 'All Job Sites' }]);
    }
  };

  const handleFilterChange = (field, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateChange = (field, date) => {
    setLocalFilters(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [field]: date
      }
    }));
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      dateRange: { start: null, end: null },
      type: 'all',
      status: 'all',
      crew: 'all',
      client: 'all',
      jobSite: 'all',
      recommendation: 'all',
      approvalStatus: 'all'
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.dateRange.start || localFilters.dateRange.end) count++;
    if (localFilters.type !== 'all') count++;
    if (localFilters.status !== 'all') count++;
    if (localFilters.crew !== 'all') count++;
    if (localFilters.client !== 'all') count++;
    if (localFilters.jobSite !== 'all') count++;
    if (localFilters.recommendation !== 'all') count++;
    if (localFilters.approvalStatus !== 'all') count++;
    return count;
  };

  const removeFilter = (filterType) => {
    const newFilters = { ...localFilters };
    switch (filterType) {
      case 'dateRange':
        newFilters.dateRange = { start: null, end: null };
        break;
      case 'type':
      case 'status':
      case 'crew':
      case 'client':
      case 'jobSite':
      case 'recommendation':
      case 'approvalStatus':
        newFilters[filterType] = 'all';
        break;
    }
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <Box className={classes.root}>
      <Box className={classes.filterHeader}>
        <Box display="flex" alignItems="center">
          <FilterList />
          <Box ml={1}>
            Filters {activeFilterCount > 0 && `(${activeFilterCount} active)`}
          </Box>
        </Box>
        <IconButton
          onClick={() => setExpanded(!expanded)}
          size="small"
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="Start Date"
              value={localFilters.dateRange.start}
              onChange={(date) => handleDateChange('start', date)}
              format="MM/dd/yyyy"
              fullWidth
              clearable
              renderInput={(props) => <TextField {...props} />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="End Date"
              value={localFilters.dateRange.end}
              onChange={(date) => handleDateChange('end', date)}
              format="MM/dd/yyyy"
              fullWidth
              clearable
              minDate={localFilters.dateRange.start}
              renderInput={(props) => <TextField {...props} />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl className={classes.formControl}>
              <InputLabel>Type</InputLabel>
              <Select
                value={localFilters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="dead">Dead</MenuItem>
                <MenuItem value="dying">Dying</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl className={classes.formControl}>
              <InputLabel>Status</InputLabel>
              <Select
                value={localFilters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="IDENTIFIED">Identified</MenuItem>
                <MenuItem value="IMAGE_CAPTURED">Image Captured</MenuItem>
                <MenuItem value="LOCATED">Located</MenuItem>
                <MenuItem value="REMOVED">Removed</MenuItem>
                <MenuItem value="IRRIGATION_CONFIRMED">Irrigation Confirmed</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl className={classes.formControl}>
              <InputLabel>Crew</InputLabel>
              <Select
                value={localFilters.crew}
                onChange={(e) => handleFilterChange('crew', e.target.value)}
              >
                {crews.map(crew => (
                  <MenuItem key={crew._id} value={crew._id}>
                    {crew.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl className={classes.formControl}>
              <InputLabel>Client</InputLabel>
              <Select
                value={localFilters.client}
                onChange={(e) => handleFilterChange('client', e.target.value)}
              >
                {clients.map(client => (
                  <MenuItem key={client._id} value={client._id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl className={classes.formControl}>
              <InputLabel>Recommendation</InputLabel>
              <Select
                value={localFilters.recommendation}
                onChange={(e) => handleFilterChange('recommendation', e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="revive">Revive</MenuItem>
                <MenuItem value="remove">Remove</MenuItem>
                <MenuItem value="replace">Replace</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl className={classes.formControl}>
              <InputLabel>Approval Status</InputLabel>
              <Select
                value={localFilters.approvalStatus}
                onChange={(e) => handleFilterChange('approvalStatus', e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Needs Approval</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box className={classes.actionButtons}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Search />}
                onClick={handleApplyFilters}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                startIcon={<Clear />}
                onClick={handleClearFilters}
                disabled={activeFilterCount === 0}
              >
                Clear All
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Collapse>

      {activeFilterCount > 0 && !expanded && (
        <Box className={classes.filterChips}>
          {(localFilters.dateRange.start || localFilters.dateRange.end) && (
            <Chip
              label={`Date: ${localFilters.dateRange.start ? localFilters.dateRange.start.toLocaleDateString() : '∞'} - ${localFilters.dateRange.end ? localFilters.dateRange.end.toLocaleDateString() : '∞'}`}
              onDelete={() => removeFilter('dateRange')}
              size="small"
            />
          )}
          {localFilters.type !== 'all' && (
            <Chip
              label={`Type: ${localFilters.type}`}
              onDelete={() => removeFilter('type')}
              size="small"
            />
          )}
          {localFilters.status !== 'all' && (
            <Chip
              label={`Status: ${localFilters.status}`}
              onDelete={() => removeFilter('status')}
              size="small"
            />
          )}
          {localFilters.crew !== 'all' && (
            <Chip
              label={`Crew: ${crews.find(c => c._id === localFilters.crew)?.name}`}
              onDelete={() => removeFilter('crew')}
              size="small"
            />
          )}
          {localFilters.client !== 'all' && (
            <Chip
              label={`Client: ${clients.find(c => c._id === localFilters.client)?.name}`}
              onDelete={() => removeFilter('client')}
              size="small"
            />
          )}
          {localFilters.jobSite !== 'all' && (
            <Chip
              label={`Job Site: ${jobSites.find(s => s._id === localFilters.jobSite)?.name}`}
              onDelete={() => removeFilter('jobSite')}
              size="small"
            />
          )}
          {localFilters.recommendation !== 'all' && (
            <Chip
              label={`Recommendation: ${localFilters.recommendation}`}
              onDelete={() => removeFilter('recommendation')}
              size="small"
            />
          )}
          {localFilters.approvalStatus !== 'all' && (
            <Chip
              label={`Approval: ${localFilters.approvalStatus === 'pending' ? 'Needs Approval' : 'Approved'}`}
              onDelete={() => removeFilter('approvalStatus')}
              size="small"
            />
          )}
        </Box>
      )}
    </Box>
  );
}